/**
 * Custom Subscription Plan Constants and Utilities
 *
 * This module provides constants and helper functions for managing custom subscription plans.
 * Custom plans allow administrators to override standard feature quantities (AI Agent credits
 * and Monthly Active Contacts) on a per-bill-record basis.
 */

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * Represents a custom usage feature for API requests and responses.
 * featureId can be any string to accommodate backend-managed features.
 */
export type CustomUsageFeature = {
  featureId: string;
  quantity: number;
  maximumQuantity?: number | null;
};

export type UserInputFeatureId =
  | 'ai_agents_total_usage'
  | 'monthly_active_contacts';

export type PlanTier = 'pro' | 'premium' | 'enterprise';

export type PlanVersion = 'v10' | 'v11';

// ============================================================================
// Constants
// ============================================================================

export const CUSTOM_PLAN_IDS: readonly string[] = [
  'sleekflow_v10_pro_custom',
  'sleekflow_v10_premium_custom',
  'sleekflow_v10_enterprise_custom',
  'sleekflow_v11_pro_custom',
  'sleekflow_v11_premium_custom',
  'sleekflow_v11_enterprise_custom'
] as const;

export const V10_CUSTOM_PLAN_IDS: readonly string[] = [
  'sleekflow_v10_pro_custom',
  'sleekflow_v10_premium_custom',
  'sleekflow_v10_enterprise_custom'
] as const;

export const V11_CUSTOM_PLAN_IDS: readonly string[] = [
  'sleekflow_v11_pro_custom',
  'sleekflow_v11_premium_custom',
  'sleekflow_v11_enterprise_custom'
] as const;

export const FEATURE_IDS = {
  AI_AGENT: 'ai_agents_total_usage',
  AI_AGENT_LIMIT: 'ai_agents_limit',
  AI_AGENT_KNOWLEDGE_BASE: 'ai_agents_knowledge_base_total_character_count',
  MONTHLY_ACTIVE_CONTACTS: 'monthly_active_contacts'
} as const;

export const USER_INPUTABLE_FEATURE_IDS = [
  FEATURE_IDS.AI_AGENT,
  FEATURE_IDS.MONTHLY_ACTIVE_CONTACTS
] as const;

// ============================================================================
// Helper Functions
// ============================================================================

export function isCustomPlan(planId: string | null | undefined): boolean {
  if (!planId) return false;
  return CUSTOM_PLAN_IDS.includes(planId);
}

export function isBillRecordCustomized(
  billRecord: { isCustomized?: boolean } | null | undefined
): boolean {
  return billRecord?.isCustomized === true;
}

export function isPlanV10(planId: string | null | undefined): boolean {
  if (!planId) return false;
  return planId.includes('_v10_');
}

export function isPlanV11(planId: string | null | undefined): boolean {
  if (!planId) return false;
  return planId.includes('_v11_');
}

export function getCustomPlanTier(
  planId: string | null | undefined
): PlanTier | null {
  if (!planId || !isCustomPlan(planId)) return null;
  const lowerPlanId = planId.toLowerCase();
  if (lowerPlanId.includes('_pro_')) return 'pro';
  if (lowerPlanId.includes('_premium_')) return 'premium';
  if (lowerPlanId.includes('_enterprise_')) return 'enterprise';
  return null;
}

export function getPlanVersion(
  planId: string | null | undefined
): PlanVersion | null {
  if (!planId) return null;
  if (isPlanV10(planId)) return 'v10';
  if (isPlanV11(planId)) return 'v11';
  return null;
}

export function isV10CustomPlan(planId: string | null | undefined): boolean {
  if (!planId) return false;
  return V10_CUSTOM_PLAN_IDS.includes(planId);
}

export function isV11CustomPlan(planId: string | null | undefined): boolean {
  if (!planId) return false;
  return V11_CUSTOM_PLAN_IDS.includes(planId);
}

export function filterOutCustomPlans(planIds: string[]): string[] {
  return planIds.filter((planId) => !isCustomPlan(planId));
}

export function getTierDisplayName(tier: PlanTier): string {
  return tier.charAt(0).toUpperCase() + tier.slice(1);
}

export function getFeatureDisplayName(featureId: string): string {
  const displayNames: Record<string, string> = {
    ai_agents_total_usage: 'AgentFlow Credit Limit',
    ai_agents_limit: 'Number of AI Agents Limit',
    ai_agents_knowledge_base_total_character_count:
      'Characters to Train On Limit',
    monthly_active_contacts: 'MAC Usage Limit'
  };
  return displayNames[featureId] || featureId;
}

// ============================================================================
// Validation Helpers
// ============================================================================

export function getRequiredFeatures(
  planId: string | null | undefined,
  isAiPlan: boolean | null
): {
  requiresAiAgent: boolean;
  requiresMAC: boolean;
} {
  if (!planId || !isCustomPlan(planId)) {
    return { requiresAiAgent: false, requiresMAC: false };
  }

  const isV10 = isPlanV10(planId);
  const isV11 = isPlanV11(planId);

  return {
    requiresAiAgent: isAiPlan === true && (isV10 || isV11),
    requiresMAC: isV11
  };
}

/**
 * Validates custom usage features array for user input.
 * Users can only submit: 'ai_agents_total_usage' and 'monthly_active_contacts'
 */
export function validateCustomUsages(
  customUsages: CustomUsageFeature[],
  planId: string | null | undefined,
  isAiPlan: boolean | null
): { isValid: boolean; error?: string } {
  if (!planId || !isCustomPlan(planId)) {
    return { isValid: false, error: 'Not a custom plan' };
  }

  const required = getRequiredFeatures(planId, isAiPlan);

  // Check for duplicate feature IDs
  const featureIds = customUsages.map((f) => f.featureId);
  const hasDuplicates = featureIds.length !== new Set(featureIds).size;
  if (hasDuplicates) {
    return { isValid: false, error: 'Duplicate feature IDs found' };
  }

  // Check for invalid feature IDs (only user-inputable features allowed)
  const hasInvalidFeatures = customUsages.some(
    (f) => !USER_INPUTABLE_FEATURE_IDS.includes(f.featureId as any)
  );
  if (hasInvalidFeatures) {
    return {
      isValid: false,
      error:
        'Only AI Agent Credits and Monthly Active Contacts can be edited by users'
    };
  }

  // Check for negative quantities
  const hasNegativeQuantities = customUsages.some((f) => f.quantity < 0);
  if (hasNegativeQuantities) {
    return { isValid: false, error: 'Quantities must be >= 0' };
  }

  // Check required features
  const hasAiAgent = customUsages.some(
    (f) => f.featureId === 'ai_agents_total_usage'
  );
  const hasMAC = customUsages.some(
    (f) => f.featureId === 'monthly_active_contacts'
  );

  if (required.requiresAiAgent && !hasAiAgent) {
    return { isValid: false, error: 'AI Agent is required for AI plans' };
  }

  if (required.requiresMAC && !hasMAC) {
    return {
      isValid: false,
      error: 'Monthly Active Contacts is required for V11 plans'
    };
  }

  return { isValid: true };
}

export function isAiPlanFromCustomUsages(
  customUsages: CustomUsageFeature[] | null | undefined
): boolean {
  if (!customUsages || customUsages.length === 0) {
    return false;
  }
  return customUsages.some(
    (usage) => usage.featureId === FEATURE_IDS.AI_AGENT_LIMIT
  );
}

